import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import type { FileModel, InformationRequestQuestion } from 'api';
import { ApiService } from 'api';
import type { RequestedFileFulfillmentStatus } from 'common/types/files/files';
import { VALUATION_TYPE_409A } from 'dashboard409a/common/constants/components';
import { useOnboardingEmailTemplates } from 'dashboard409a/hooks/useOnboardingEmailTemplates';
import { createIndividualDocument, getCurrentInformationRequests, getReportContacts } from 'dashboard409a/services/dashboard';
import { RootStateType } from 'dashboard409a/states/store';
import {
  contactSelectKey,
  getDefaultInformationRequestExpirationDate,
  getInformationRequestExpirationFromRequests,
  isValidInformationRequestExpirationDate,
  parseInformationRequestExpirationDate,
  resolveContactFirstName,
  toInformationRequestExpirationIso,
} from 'dashboard409a/utilities';
import FilesService from 'services/documents';
import { Logger } from 'utilities';
import {
  isOnboardingDocumentFulfilled,
  type OnboardingRequestedFile,
  toOnboardingRequestedFiles,
} from './OnboardingEmailModal.fulfillment';
import { OnboardingEmailModalProps } from './OnboardingEmailModal.props';
import { DocumentChecklistItem } from './OnboardingEmailModal.types';
import {
  composeOnboardingEmail,
  formatCombinedEmail,
  namesMatch,
  normalizeDocumentName,
  parseCombinedEmail,
  replaceDocumentPortalLink,
} from './OnboardingEmailModal.utils';

const filesService = new FilesService();

const NOT_REQUESTED_FULFILLMENT_NOTE = 'Not requested for this valuation.';

type UseOnboardingEmailModalParams = Pick<
  OnboardingEmailModalProps,
  'open' | 'cmdId' | 'companyId' | 'companyName' | 'measurementDateId'
>;

// Map normalized RequestedFile.name -> request row for fulfillment and uploads.
const collectRequestedFilesByName = (
  files: OnboardingRequestedFile[] | undefined
): Map<string, OnboardingRequestedFile> => {
  const map = new Map<string, OnboardingRequestedFile>();
  (files ?? []).forEach(requestedFile => {
    map.set(normalizeDocumentName(requestedFile.name), requestedFile);
  });
  return map;
};

const collectUploadedFilesByName = (
  files: OnboardingRequestedFile[] | undefined
): Map<string, FileModel[]> => {
  const map = new Map<string, FileModel[]>();
  (files ?? []).forEach(requestedFile => {
    const uploadedFiles = requestedFile.files ?? [];
    const hasUploaded = Boolean(requestedFile.is_uploaded) || uploadedFiles.length > 0;
    if (hasUploaded && uploadedFiles.length > 0) {
      map.set(normalizeDocumentName(requestedFile.name), uploadedFiles);
    }
  });
  return map;
};

const toBackendQuestionChecklistItems = (
  questions: InformationRequestQuestion[] | undefined
): DocumentChecklistItem[] =>
  (questions ?? []).map(question => {
    // Show every child question (answered or not) with its answer when present.
    const childQuestions = (question.children_questions ?? []).map(child => ({
      label: child.name,
      answered: Boolean(child.answered),
      answerDisplay: child.answer_display?.trim() || null,
    }));
    return {
      label: question.name,
      name: question.name,
      description: question.description ?? undefined,
      kind: 'question' as const,
      checked: true,
      alreadyHave: Boolean(question.answered),
      answerDisplay: question.answer_display ?? null,
      childQuestions,
    };
  });

const useOnboardingEmailModal = ({
  open,
  cmdId,
  companyName,
}: UseOnboardingEmailModalParams) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  const {
    templates,
    presets,
    selectedId: selectedTemplateId,
    setSelectedId: setSelectedTemplateId,
    selectedTemplate,
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
  } = useOnboardingEmailTemplates();

  const [checklist, setChecklist] = useState<DocumentChecklistItem[]>([]);
  const checklistRef = useRef<DocumentChecklistItem[]>([]);
  checklistRef.current = checklist;
  // Track template identity so fulfillment/portal refreshes keep analyst
  // checkmarks, while switching templates still resets to defaults.
  const checklistTemplateIdRef = useRef<string | null>(null);
  const [selectedContactKey, setSelectedContactKey] = useState<string>('');
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [portalLink, setPortalLink] = useState<string | null>(null);
  const lastSavedEmailRef = useRef<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    getDefaultInformationRequestExpirationDate()
  );
  const expirationDateRef = useRef(expirationDate);
  expirationDateRef.current = expirationDate;

  // Subject + body are edited separately but persisted as one combined string.
  const draftEmail = useMemo(
    () => formatCombinedEmail(draftSubject, draftBody),
    [draftSubject, draftBody]
  );

  const retrieveInformationRequestStatus = useCallback(async () => {
    if (!cmdId) {
      return null;
    }
    const existing = await ApiService.apiRetrieveInformationRequestStatusRead(String(cmdId));
    return existing.results[0] ?? null;
  }, [cmdId]);

  const { data: savedDraft, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ['onboarding-email-saved-draft', cmdId],
    queryFn: async () => {
      if (!cmdId) {
        return null;
      }
      try {
        const [status, portal, currentRequests] = await Promise.all([
          retrieveInformationRequestStatus(),
          ApiService.apiTasksPortalLinksRead(String(cmdId)),
          getCurrentInformationRequests(cmdId).catch(() => []),
        ]);
        return {
          id: status?.id ?? null,
          draftedEmail: status?.drafted_email ?? '',
          portalLink: portal.document_portal_link ?? null,
          files: toOnboardingRequestedFiles(status?.files),
          questions: status?.questions ?? [],
          expirationDate: getInformationRequestExpirationFromRequests(currentRequests) ?? null,
        };
      } catch (error) {
        Logger.error('Failed to load saved onboarding email draft:', error);
        return {
          id: null,
          draftedEmail: '',
          portalLink: null,
          files: [] as OnboardingRequestedFile[],
          questions: [] as InformationRequestQuestion[],
          expirationDate: null as string | null,
        };
      }
    },
    enabled: open && Boolean(cmdId),
  });

  // Same Attio-backed people list used by the final report email.
  const { data: contacts = [], isLoading: isLoadingContacts } = useQuery({
    queryKey: ['onboarding-email-contacts', cmdId],
    queryFn: () => getReportContacts(cmdId as number),
    enabled: open && Boolean(cmdId),
  });

  const selectedContact = useMemo(
    () =>
      contacts.find((contact, index) => contactSelectKey(contact, index) === selectedContactKey) ??
      null,
    [contacts, selectedContactKey]
  );

  const contactFirstName = useMemo(
    () => resolveContactFirstName(selectedContact),
    [selectedContact]
  );

  // Keep selection valid when contacts reload; auto-pick the primary contact once available.
  useEffect(() => {
    if (!open || contacts.length === 0) {
      return;
    }
    const keys = contacts.map((contact, index) => contactSelectKey(contact, index));
    if (selectedContactKey && keys.includes(selectedContactKey)) {
      return;
    }
    setSelectedContactKey(keys[0] ?? '');
  }, [open, contacts, selectedContactKey]);

  const requestedFilesByName = useMemo(
    () => collectRequestedFilesByName(savedDraft?.files),
    [savedDraft?.files]
  );
  const uploadedFilesByName = useMemo(
    () => collectUploadedFilesByName(savedDraft?.files),
    [savedDraft?.files]
  );
  const requestQuestions = useMemo(
    () => savedDraft?.questions ?? [],
    [savedDraft?.questions]
  );
  const isFollowUp = Boolean(savedDraft?.portalLink);

  useEffect(() => {
    if (!open || !selectedTemplate) {
      checklistTemplateIdRef.current = null;
      return;
    }

    const templateChanged = checklistTemplateIdRef.current !== selectedTemplate.id;
    checklistTemplateIdRef.current = selectedTemplate.id;

    const documentItems: DocumentChecklistItem[] = selectedTemplate.documents
      .filter(document => (document.kind ?? 'document') === 'document')
      .map(document => {
        const requestedFile = requestedFilesByName.get(normalizeDocumentName(document.name));
        const uploadedFiles =
          uploadedFilesByName.get(normalizeDocumentName(document.name)) ?? [];
        const alreadyHave =
          isOnboardingDocumentFulfilled(requestedFile) || uploadedFiles.length > 0;
        return {
          label: document.label,
          name: document.name,
          description: document.description,
          kind: 'document' as const,
          checked: !alreadyHave && (document.defaultChecked ?? true),
          alreadyHave,
          uploadedFiles,
          requestedFileId: requestedFile?.id,
          fulfillmentStatus: requestedFile?.fulfillment_status,
          fulfillmentNote: requestedFile?.fulfillment_note ?? null,
        };
      });

    const backendQuestionItems = toBackendQuestionChecklistItems(requestQuestions);
    const backendQuestionNames = backendQuestionItems.map(item => item.name);

    // Keep template-only questions that are not already represented on the request
    // (e.g. Term sheets when it is not a seeded default question).
    const unmatchedTemplateQuestions: DocumentChecklistItem[] = selectedTemplate.documents
      .filter(document => (document.kind ?? 'document') === 'question')
      .filter(
        document =>
          !backendQuestionNames.some(
            backendName =>
              namesMatch(backendName, document.name) || namesMatch(backendName, document.label)
          )
      )
      .map(document => ({
        label: document.label,
        name: document.name,
        description: document.description,
        kind: 'question' as const,
        checked: document.defaultChecked ?? true,
        alreadyHave: false,
      }));

    const nextItems = [...documentItems, ...backendQuestionItems, ...unmatchedTemplateQuestions];

    // Portal/draft refreshes rebuild rows (fulfillment, uploads) but must not
    // re-check documents the analyst just unchecked before regenerating.
    setChecklist(prev => {
      if (templateChanged || prev.length === 0) {
        return nextItems;
      }
      const previousCheckedByName = new Map(prev.map(item => [item.name, item.checked]));
      return nextItems.map(item => {
        if (item.alreadyHave) {
          return { ...item, checked: false };
        }
        if (!previousCheckedByName.has(item.name)) {
          return item;
        }
        return { ...item, checked: Boolean(previousCheckedByName.get(item.name)) };
      });
    });
  }, [open, selectedTemplate, requestedFilesByName, uploadedFilesByName, requestQuestions]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!cmdId) {
      setDraftSubject('');
      setDraftBody('');
      setPortalLink(null);
      lastSavedEmailRef.current = null;
      return;
    }
    if (savedDraft) {
      const { subject, body } = parseCombinedEmail(savedDraft.draftedEmail);
      const syncedBody = savedDraft.portalLink
        ? replaceDocumentPortalLink(body, savedDraft.portalLink)
        : body;
      setDraftSubject(subject);
      setDraftBody(syncedBody);
      setPortalLink(savedDraft.portalLink);
      // Keep lastSaved as the server blob so a rewritten portal URL autosaves.
      lastSavedEmailRef.current = formatCombinedEmail(subject, body);
    }
  }, [open, cmdId, savedDraft]);

  // Prefill the stored deadline on follow-up; reset to 14 days when the modal closes.
  useEffect(() => {
    if (!open) {
      setExpirationDate(getDefaultInformationRequestExpirationDate());
      return;
    }
    if (savedDraft?.expirationDate) {
      setExpirationDate(parseInformationRequestExpirationDate(savedDraft.expirationDate));
    }
  }, [open, savedDraft?.expirationDate]);

  const statusId = savedDraft?.id ?? null;

  const { mutate: persistDraftEmail } = useMutation({
    mutationFn: async (email: string) => {
      if (!statusId) {
        return null;
      }
      await ApiService.apiInformationRequestStatusDraftedEmailPartialUpdate(String(statusId), {
        id: statusId,
        drafted_email: email,
      });
      return email;
    },
    onSuccess: email => {
      if (email == null) {
        return;
      }
      lastSavedEmailRef.current = email;
      queryClient.setQueryData(
        ['onboarding-email-saved-draft', cmdId],
        (previous: typeof savedDraft) => (previous ? { ...previous, draftedEmail: email } : previous)
      );
    },
    onError: (error: Error) => {
      Logger.error('Failed to save onboarding email draft:', error);
      enqueueSnackbar('Failed to save your email edits.', { variant: 'error' });
    },
  });

  // Persist manual email edits immediately (used on blur / before closing).
  const flushDraftEmail = useCallback(() => {
    if (!statusId) {
      return;
    }
    if (draftEmail === lastSavedEmailRef.current) {
      return;
    }
    persistDraftEmail(draftEmail);
  }, [statusId, draftEmail, persistDraftEmail]);

  // Debounced autosave while the user is typing.
  useEffect(() => {
    if (!open || !statusId) {
      return undefined;
    }
    if (draftEmail === lastSavedEmailRef.current) {
      return undefined;
    }
    const handle = setTimeout(() => {
      persistDraftEmail(draftEmail);
    }, 800);
    return () => clearTimeout(handle);
  }, [open, statusId, draftEmail, persistDraftEmail]);

  const toggleDocument = useCallback((documentName: string) => {
    setChecklist(prev =>
      prev.map(item =>
        item.name === documentName && !item.alreadyHave
          ? { ...item, checked: !item.checked }
          : item
      )
    );
  }, []);

  const requestedDocuments = useMemo(
    () => checklist.filter(item => item.checked && !item.alreadyHave),
    [checklist]
  );

  const requestedPortalDocuments = useMemo(
    () => requestedDocuments.filter(item => (item.kind ?? 'document') === 'document'),
    [requestedDocuments]
  );

  const ensureInformationRequestStatus = useCallback(async () => {
    if (!cmdId) {
      throw new Error('Missing company measurement date');
    }

    const existing = await retrieveInformationRequestStatus();
    if (existing?.id) {
      return existing;
    }

    // Skip firm/company document-template seeding so this request contains
    // only the documents selected from the client-side onboarding template.
    return ApiService.apiCreateInformationRequestStatusCreate({
      report_date: cmdId,
      valuation_type: VALUATION_TYPE_409A,
      seed_documents_from_templates: false,
    });
  }, [cmdId, retrieveInformationRequestStatus]);

  const { mutateAsync: markDocumentFulfillment, isPending: isMarkingDocumentNa } = useMutation({
    mutationFn: async ({
      documentName,
      fulfillmentStatus,
      fulfillmentNote,
      requestedFileId,
    }: {
      documentName: string;
      fulfillmentStatus: RequestedFileFulfillmentStatus;
      fulfillmentNote?: string | null;
      requestedFileId?: number;
    }) => {
      const status = await ensureInformationRequestStatus();
      if (!status.id) {
        throw new Error('Failed to resolve information request status');
      }

      let resolvedRequestedFileId = requestedFileId;
      if (!resolvedRequestedFileId) {
        const created = await ApiService.apiDocumentsRequestedFileCreate({
          name: documentName,
          company_information_request_status: status.id,
        });
        resolvedRequestedFileId = created.id;
      }
      if (!resolvedRequestedFileId) {
        throw new Error('Failed to resolve requested document');
      }

      await filesService.markRequestedFileFulfillment(resolvedRequestedFileId, {
        fulfillment_status: fulfillmentStatus,
        fulfillment_note: fulfillmentNote ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-email-saved-draft', cmdId] });
      enqueueSnackbar('Document marked successfully.', { variant: 'success' });
    },
    onError: (error: Error) => {
      Logger.error('Failed to mark onboarding document N/A:', error);
      enqueueSnackbar(error.message || 'Failed to update document status.', { variant: 'error' });
    },
  });

  const markDocumentAsNa = useCallback(
    async (
      item: DocumentChecklistItem,
      fulfillmentStatus: RequestedFileFulfillmentStatus,
      fulfillmentNote?: string | null
    ) => {
      if (item.kind !== 'document' || item.alreadyHave) {
        return;
      }
      await markDocumentFulfillment({
        documentName: item.name,
        fulfillmentStatus,
        fulfillmentNote,
        requestedFileId: item.requestedFileId,
      });
    },
    [markDocumentFulfillment]
  );

  // Unchecking an item marks it N/A on generate, so analysts need a way back:
  // clearing fulfillment puts it back on the portal as outstanding.
  const { mutateAsync: restoreDocumentRequest, isPending: isRestoringDocument } = useMutation({
    mutationFn: async (requestedFileId: number) => {
      await filesService.markRequestedFileFulfillment(requestedFileId, {
        fulfillment_status: 'pending',
        fulfillment_note: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-email-saved-draft', cmdId] });
      enqueueSnackbar('Document added back to the request.', { variant: 'success' });
    },
    onError: (error: Error) => {
      Logger.error('Failed to restore onboarding document request:', error);
      enqueueSnackbar('Failed to add the document back to the request.', { variant: 'error' });
    },
  });

  const requestDocumentAgain = useCallback(
    async (item: DocumentChecklistItem) => {
      if (!item.requestedFileId) {
        return;
      }
      await restoreDocumentRequest(item.requestedFileId);
    },
    [restoreDocumentRequest]
  );

  const { mutate: generateEmail, isPending: isGenerating } = useMutation({
    mutationFn: async () => {
      if (!cmdId) {
        throw new Error('Missing company measurement date');
      }
      if (!selectedTemplate) {
        throw new Error('No onboarding template selected');
      }
      if (!currentUserId) {
        throw new Error('Missing current user');
      }

      // Read checklist at call time so unchecks are never lost to a stale closure.
      const currentChecklist = checklistRef.current;
      const documentsForEmail = currentChecklist.filter(
        item => item.checked && !item.alreadyHave
      );
      const portalDocumentsToRequest = documentsForEmail.filter(
        item => (item.kind ?? 'document') === 'document'
      );
      const portalDocumentsToMarkNa = currentChecklist.filter(
        item =>
          item.kind === 'document' &&
          !item.checked &&
          !item.alreadyHave &&
          Boolean(item.requestedFileId)
      );

      // First send requires at least one still-needed document. Follow-up
      // regenerate can run even when every document is already provided.
      if (!isFollowUp && portalDocumentsToRequest.length === 0) {
        throw new Error('Select at least one document that still needs to be requested');
      }

      const status = await ensureInformationRequestStatus();
      if (!status.id) {
        throw new Error('Failed to resolve information request status');
      }

      const existingNames = new Set(
        (status.files ?? []).map(file => normalizeDocumentName(file.name))
      );

      // Push selected still-needed docs that are not already on the draft status.
      // Questions are email/checklist-only and are not created as RequestedFile rows.
      const documentsToCreate = portalDocumentsToRequest.filter(
        document => !existingNames.has(normalizeDocumentName(document.name))
      );
      await Promise.all(
        documentsToCreate.map(document =>
          createIndividualDocument({
            name: document.name,
            company_information_request_status: status.id as number,
          })
        )
      );

      // Deselected documents stay on the request but are marked N/A so the
      // portal shows them as not required instead of still pending.
      await Promise.all(
        portalDocumentsToMarkNa.map(document =>
          filesService.markRequestedFileFulfillment(document.requestedFileId as number, {
            fulfillment_status: 'not_applicable',
            fulfillment_note: NOT_REQUESTED_FULFILLMENT_NOTE,
          })
        )
      );

      const existingPortal = await ApiService.apiTasksPortalLinksRead(String(cmdId));
      const hasExistingRequest = Boolean(existingPortal.document_portal_link);
      const selectedExpirationDate = expirationDateRef.current;
      const expirationPayload = isValidInformationRequestExpirationDate(selectedExpirationDate)
        ? { expiration_date: toInformationRequestExpirationIso(selectedExpirationDate) }
        : {};

      if (hasExistingRequest) {
        await ApiService.apiUpdateInformationRequestStatusUpdate(String(status.id), {
          id: status.id,
          responsible_users: [currentUserId],
          send_email: false,
          ...expirationPayload,
        });
      } else {
        await ApiService.apiCreateInformationRequestUpdate(String(status.id), {
          responsible_users: [currentUserId],
          send_email: false,
          ...expirationPayload,
        });
      }

      const portal = await ApiService.apiTasksPortalLinksRead(String(cmdId));
      const documentPortalLink = portal.document_portal_link;
      if (!documentPortalLink) {
        throw new Error('Document request portal link was not created');
      }

      // Email body matches the client-side template selection exactly
      // (checked + not already provided), including per-document descriptions.
      const email = composeOnboardingEmail({
        template: selectedTemplate,
        companyName,
        contactFirstName,
        portalLink: documentPortalLink,
        requestedDocuments: documentsForEmail,
      });

      await ApiService.apiInformationRequestStatusDraftedEmailPartialUpdate(String(status.id), {
        id: status.id,
        drafted_email: email,
      });

      return {
        portalLink: documentPortalLink,
        email,
        statusId: status.id,
      };
    },
    onSuccess: result => {
      setPortalLink(result.portalLink);
      const { subject, body } = parseCombinedEmail(result.email);
      setDraftSubject(subject);
      setDraftBody(body);
      lastSavedEmailRef.current = result.email;
      // Refetch so checklist picks up seeded request questions + upload state.
      queryClient.invalidateQueries({ queryKey: ['onboarding-email-saved-draft', cmdId] });
      enqueueSnackbar('Onboarding email drafted with document request portal link.', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      Logger.error('Failed to generate onboarding email:', error);
      enqueueSnackbar(error.message || 'Failed to generate onboarding email. Please try again.', {
        variant: 'error',
      });
    },
  });

  const handleCopyEmail = useCallback(async () => {
    try {
      const emailToCopy = portalLink
        ? replaceDocumentPortalLink(draftEmail, portalLink)
        : draftEmail;
      await navigator.clipboard.writeText(emailToCopy);
      enqueueSnackbar('Email copied to clipboard', { variant: 'success' });
    } catch (error) {
      Logger.error('Failed to copy email:', error);
      enqueueSnackbar('Failed to copy email to clipboard.', { variant: 'error' });
    }
  }, [draftEmail, portalLink, enqueueSnackbar]);

  const handleCopyPortalLink = useCallback(async () => {
    if (!portalLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(portalLink);
      enqueueSnackbar('Portal link copied to clipboard', { variant: 'success' });
    } catch (error) {
      Logger.error('Failed to copy portal link:', error);
      enqueueSnackbar('Failed to copy portal link to clipboard.', { variant: 'error' });
    }
  }, [portalLink, enqueueSnackbar]);

  const canGenerate =
    Boolean(cmdId) &&
    Boolean(selectedTemplate) &&
    Boolean(currentUserId) &&
    (isFollowUp || requestedPortalDocuments.length > 0) &&
    isValidInformationRequestExpirationDate(expirationDate) &&
    !isGenerating;

  return {
    templates,
    presets,
    selectedTemplateId,
    setSelectedTemplateId,
    selectedTemplate,
    emailSubject: selectedTemplate?.emailSubject ?? '',
    introduction: selectedTemplate?.introduction ?? '',
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    checklist,
    toggleDocument,
    markDocumentAsNa,
    isMarkingDocumentNa,
    requestDocumentAgain,
    isRestoringDocument,
    isLoadingDocuments,
    contacts,
    isLoadingContacts,
    selectedContactKey,
    setSelectedContactKey,
    draftEmail,
    draftSubject,
    setDraftSubject,
    draftBody,
    setDraftBody,
    flushDraftEmail,
    portalLink,
    generateEmail,
    isGenerating,
    canGenerate,
    isFollowUp,
    expirationDate,
    setExpirationDate,
    handleCopyEmail,
    handleCopyPortalLink,
    requestedDocuments,
  };
};

export default useOnboardingEmailModal;
